import React, { MouseEvent, useEffect, useMemo, useState } from 'react';

import AddIcon from '@mui/icons-material/Add';

import { LayerContext } from 'state/LayerContext';
import { useGetDatesQuery, useGetTypicalMonthsQuery } from 'state/apiSlice';
import { useAppDispatch, useAppSelector } from 'state/hooks';
import {
  selectRmCreateRouteModal,
  selectRmRealtimeOn,
  setRMCreateRouteModal,
  setRmRealtimeOn,
} from 'state/routeMonitoringSlice';
import { selectAreRoutesReady } from 'state/savedRouteSlices';
import { ProjectFeatures } from 'state/userSlice';
import {
  getSpecificWorkflowState,
  getWorkflowState,
  selectComparison,
  selectCurrentProjectInfo,
  selectProject,
  selectRmEndDateTime,
  selectRmStartDateTime,
  selectRouteSelection,
  setComparison,
  setMaximize,
  setRmDateTimeRange,
  setRouteSelection,
  setSelectedSegmentId,
  setTargetDate,
} from 'state/workflowSlice';

import { MAXIMIZE_MAP, WorkflowType } from 'appConstants';
import { StlButton } from 'features/common/StlButton/StlButton';
import { routeSelectionIsSavedRoute } from 'features/common/hooks/useRouteInfo';
import { MapContextProvider } from 'features/map/MapContext';
import { withSegmentTooltip } from 'features/map/hooks/withSegmentTooltip';
import { ReliesOn } from 'features/workflow_common/NoDataLoadedWarning';
import { RouteInstructionsPanel } from 'features/workflow_common/RouteInstructions/RouteInstructions';

import StlModal from '../StlModal/StlModal';
import {
  DEFAULT_TIME_WITH_MERIDIEM,
  getCorrectedRmDateTimeRange,
  makeLocalDateFromStrings,
} from '../common/DateTimeSelector/utils';
import { ReactComponent as AddElements } from '../common/icons/add_elements.svg';
import { WorkflowInfo } from '../workflow_common/workflowLookup';
import { useAutoSetLayerFromTargetDate } from '../workflow_nrt/WorkflowRecent';
import { AddRouteModal } from './AddRouteModal/AddRouteModal';
import { RouteMonitoringList } from './RouteMonitoringList';
import { RouteMonitoringMap } from './RouteMonitoringMap';
import {
  useCurrentRoutesWithGeoJson,
  useRMDataProvider,
  useRealtimeRMDataProvider,
} from './hooks/useRMDataProvider';

import styles from '../workspace/Workspace.module.css';

export const MODAL_DESCR = `It looks like you don't have any saved routes yet. 
To start monitoring, simply add a new route and keep track of it effortlessly.`;
const MODAL_TITLE = `You don't have any saved routes!`;

export const workflowRouteMonitoring: WorkflowInfo = {
  dataAvailableReliesOn: [ReliesOn.nrtDay, ReliesOn.typicalMonth],
  switchToState: (fromWorkflow, state) => {
    if (
      [
        WorkflowType.Timeline,
        WorkflowType.Speed,
        WorkflowType.TravelTime,
      ].includes(fromWorkflow)
    ) {
      getWorkflowState(state).comparison = getSpecificWorkflowState(
        state,
        fromWorkflow,
      ).comparison;
      const prevTargetDate = getSpecificWorkflowState(
        state,
        fromWorkflow,
      ).targetDate;
      if (prevTargetDate) {
        getWorkflowState(state).targetDate = prevTargetDate;
        getWorkflowState(state).startDateTime =
          prevTargetDate &&
          makeLocalDateFromStrings(prevTargetDate, DEFAULT_TIME_WITH_MERIDIEM);
        getWorkflowState(state).endDateTime =
          prevTargetDate &&
          makeLocalDateFromStrings(
            prevTargetDate,
            DEFAULT_TIME_WITH_MERIDIEM,
            true,
          );
      }
    }
  },
};

const MapWithTooltip = withSegmentTooltip(RouteMonitoringMap);

export function WorkflowRouteMonitoring() {
  const dispatch = useAppDispatch();
  const project = useAppSelector(selectProject);
  const routeSelection = useAppSelector(selectRouteSelection);
  const hasRoutes = useAppSelector(selectAreRoutesReady);
  const realtime = useAppSelector(selectRmRealtimeOn);
  const startDateTime = useAppSelector(selectRmStartDateTime);
  const endDateTime = useAppSelector(selectRmEndDateTime);
  const comparison = useAppSelector(selectComparison);
  const modal = useAppSelector(selectRmCreateRouteModal);
  const userProject = useAppSelector(selectCurrentProjectInfo);
  const hasRT = userProject?.features.includes(ProjectFeatures.REALTIME);
  const [enableHover, setEnableHover] = useState<boolean>(false);

  const { data: datesInfo } = useGetDatesQuery(project);
  const { data: typicalMonthsInfo } = useGetTypicalMonthsQuery(project);
  const { refetch, isUninitialized } = useRealtimeRMDataProvider();

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const result = useRMDataProvider();
  const memoizedLayer = useAutoSetLayerFromTargetDate();
  const { isReady } = useCurrentRoutesWithGeoJson();

  const lastDate = useMemo(() => {
    if (!datesInfo || datesInfo.length === 0) return null;
    return datesInfo[datesInfo.length - 1]?.date;
  }, [datesInfo]);

  useEffect(() => {
    if (!hasRT && realtime) dispatch(setRmRealtimeOn(false));
  }, [hasRT, realtime]);

  useEffect(() => {
    if (!lastDate || !datesInfo?.length) return;

    if (!startDateTime && !endDateTime) {
      dispatch(
        setRmDateTimeRange({
          start: makeLocalDateFromStrings(lastDate, DEFAULT_TIME_WITH_MERIDIEM),
          end: makeLocalDateFromStrings(
            lastDate,
            DEFAULT_TIME_WITH_MERIDIEM,
            true,
          ),
        }),
      );
      return;
    }

    if (!startDateTime || !endDateTime) return;

    const corrected = getCorrectedRmDateTimeRange(
      startDateTime,
      endDateTime,
      datesInfo,
    );
    if (corrected) dispatch(setRmDateTimeRange(corrected));
  }, [lastDate, datesInfo, startDateTime, endDateTime]);

  useEffect(() => {
    const removeTemporaryRoute = () => {
      if (routeSelection && !routeSelectionIsSavedRoute(routeSelection)) {
        dispatch(setRouteSelection(undefined));
        dispatch(setSelectedSegmentId(undefined));
      }
    };
    dispatch(setMaximize(MAXIMIZE_MAP));
    removeTemporaryRoute();
  }, [project, dispatch]);

  useEffect(() => {
    if (startDateTime) dispatch(setTargetDate(startDateTime.split('T')[0]));
  }, [startDateTime]);

  useEffect(() => {
    if (realtime && !isUninitialized) refetch();
  }, [realtime, isUninitialized]);

  const monthComparison = useMemo(() => {
    if (!typicalMonthsInfo || typicalMonthsInfo.length === 0) return null;
    return typicalMonthsInfo[0].typicalMonth;
  }, [typicalMonthsInfo]);

  useEffect(() => {
    if (monthComparison && !comparison)
      dispatch(setComparison(monthComparison));
  }, [monthComparison]);

  const handleCreateRoute = (e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    dispatch(setRMCreateRouteModal(true));
  };

  let createRouteModal: React.ReactNode;
  if (!(hasRoutes || !isReady || !project || !memoizedLayer.layer)) {
    createRouteModal = (
      <StlModal
        title={MODAL_TITLE}
        descr={MODAL_DESCR}
        upper={<AddElements width={209} height={140} />}
        lower={
          <div>
            <StlButton
              width={156}
              handleClick={handleCreateRoute}
              isActive
              icon={<AddIcon />}
              value="Create New Route"
            />
          </div>
        }
      />
    );
  }

  return (
    <LayerContext.Provider value={memoizedLayer}>
      <MapContextProvider>
        <div
          className={styles.workspace_content}
          style={{ position: 'relative' }}
        >
          <div className={styles.workspace_container}>
            <RouteMonitoringList lastDate={lastDate} />
            {createRouteModal}
            <AddRouteModal open={modal} />
            <div
              className={styles.workspace_content}
              id={styles.route_monitoring_map}
            >
              <MapWithTooltip
                enableHover={enableHover}
                setEnableHover={setEnableHover}
              />
              <RouteInstructionsPanel
                maximizeBtn={false}
                sx={{ maxWidth: '100%' }}
                closeBtn
              />
            </div>
          </div>
        </div>
      </MapContextProvider>
    </LayerContext.Provider>
  );
}
